import { useState, useCallback } from 'react';
import { createInitialState } from '../config/constants.js';

/**
 * Manages all game state with granular updaters
 * @param {Object} initialState - Optional initial state (for loading saves)
 * @returns {Object} Game state and updater functions
 */
export function useGameState(initialState = null) {
  const [game, setGame] = useState(() => {
    return initialState || createInitialState();
  });

  const updatePhase = useCallback((phase) => {
    setGame(g => ({ ...g, phase }));
  }, []);

  const updateResources = useCallback((updates) => {
    setGame(g => ({
      ...g,
      food: updates.food ?? g.food,
      wood: updates.wood ?? g.wood,
      stone: updates.stone ?? g.stone,
      metal: updates.metal ?? g.metal,
      knowledge: updates.knowledge ?? g.knowledge,
      morale: updates.morale ?? g.morale,
      techPoints: updates.techPoints ?? g.techPoints
    }));
  }, []);

  const updateWorkers = useCallback((workers) => {
    setGame(g => ({ ...g, workers }));
  }, []);

  const assignWorker = useCallback((workerId, task) => {
    setGame(g => ({
      ...g,
      assignments: { ...g.assignments, [workerId]: task }
    }));
  }, []);

  const updateRolls = useCallback((rolls) => {
    setGame(g => ({ ...g, rolls }));
  }, []);

  const addPush = useCallback((task, pushResult) => {
    setGame(g => ({
      ...g,
      pushes: { ...g.pushes, [task]: pushResult }
    }));
  }, []);

  const setBountyTask = useCallback((task) => {
    setGame(g => ({ ...g, bountyTask: task }));
  }, []);

  const reset = useCallback(() => {
    setGame(createInitialState());
  }, []);

  return {
    game,
    setGame,
    updatePhase,
    updateResources,
    updateWorkers,
    assignWorker,
    updateRolls,
    addPush,
    setBountyTask,
    reset
  };
}
